import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  Button,
  Dialog,
  DialogTitle,
  makeStyles,
  Tab,
  Tabs,
  Typography,
  Grid,
} from '@material-ui/core';
import {
  CloseOutlined,
  FileCopyOutlined,
  HighlightOffOutlined,
  PrintOutlined,
} from '@material-ui/icons';
import { closeAll, closeDoc } from 'store/actions/lightboxActions';
import useCustomNotify from 'hooks/useCustomNotify';
import printPdf from '../../../services/helpers/printPDF';
import ReactHtmlParser from 'react-html-parser';
import clipboardCopy from 'clipboard-copy';
import Lottie from 'react-lottie';
import PrintLoadingAnimation from 'assets/animations/animation-print-loading.json';

declare const InstallTrigger: any;

const printLoadingAnimationOptions = {
  loop: true,
  autoplay: true,
  animationData: PrintLoadingAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid meet',
  },
};

const useStyles = makeStyles((theme) => ({
  dialog: {
    overflow: 'hidden',
  },

  labelDialog: {
    width: 620,
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100% - 32px)',
    margin: 16,
    borderRadius: 16,
    boxShadow: '0 16px 32px -8px rgba(0, 0, 0, 0.25)',
  },

  closeButton: {
    position: 'absolute',
    minWidth: 0,
    right: theme.spacing(2.5),
    top: theme.spacing(1.75),
    color: theme.palette.secondary.contrastText,
  },

  labelHeader: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minHeight: 42,
    padding: theme.spacing(1.5, 2.625),
    backgroundColor: theme.palette.background.paper,
  },

  labelCloseButton: {
    width: 18,
    minWidth: 18,
    height: 18,
    padding: 0,
    color: theme.palette.secondary.contrastText,

    '& .MuiSvgIcon-root': {
      fontSize: 18,
    },
  },

  title: {
    display: 'flex',
    alignItems: 'center',
    // paddingRight: theme.spacing(8),
    flexWrap: 'wrap',
  },

  actionButton: {
    marginLeft: theme.spacing(4),
  },

  content: {
    position: 'relative',
    width: theme.spacing(75),
    marginBottom: theme.spacing(2),
  },

  labelContent: {
    position: 'relative',
    width: '100%',
    marginBottom: 0,
  },

  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4, 0),
  },

  loadingAnimation: {
    width: 300,
    height: 300,

    [theme.breakpoints.down('xs')]: {
      width: 240,
      height: 240,
    },
  },

  embedView: {
    height: theme.spacing(75),
  },

  labelEmbedView: {
    height: 'calc(100vh - 163px)',
    maxHeight: 577,
  },

  labelInternationalEmbedView: {
    height: 'calc(100vh - 350px)',
    maxHeight: 400,
  },

  internationalEmbedView: {
    '@media (min-width: 1601px)': {
      height: theme.spacing(75),
    },
    '@media (max-width: 1600px)': {
      height: theme.spacing(50),
    },
  },

  docView: {
    position: 'relative',
    maxHeight: '56.25%',
    overflow: 'hidden',
    backgroundColor: '#555',
    fontSize: 0,
    width: '100%',
  },

  page: {
    display: 'inline-block',
    margin: theme.spacing(0.5),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },

  frame: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    width: '100%',
    height: '100%',
    border: 'none',
  },

  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
  },

  tabClose: {
    marginLeft: theme.spacing(1),
    color: theme.palette.primary.light,

    '&:hover': {
      color: theme.palette.primary.main,
    },
  },

  internationalButton: {
    height: 56,
    marginTop: theme.spacing(2),
    '&.MuiButton-containedSecondary': {
      backgroundColor: theme.palette.secondary.light,

      '&:hover': {
        backgroundColor: theme.palette.primary.light,
      },
    },
  },

  internationalNotice: {
    marginTop: theme.spacing(2),
    '& ol': {
      paddingLeft: theme.spacing(2),
    },
  },

  labelInternationalNotice: {
    padding: theme.spacing(0, 2.625, 2),
    backgroundColor: theme.palette.background.paper,

    '& ol': {
      paddingLeft: theme.spacing(2),
    },
  },

  labelFooter: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 89,
    padding: theme.spacing(2, 2.625),
    backgroundColor: theme.palette.background.paper,

    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',

      '& > :not(:first-child)': {
        marginTop: theme.spacing(2),
      },
    },
  },

  labelCopyButton: {
    width: 250,
    height: 56,
    borderRadius: 500,
    borderColor: '#E1E1E1',
    color: theme.palette.secondary.contrastText,
    fontSize: 16,
    lineHeight: '20px',
    textTransform: 'none',

    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(1.25),
    },

    '& .MuiSvgIcon-root': {
      width: 19,
      height: 20,
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },

  labelFooterActions: {
    display: 'flex',
    width: 309,
    height: 56,
    marginLeft: 'auto',

    '& > :not(:first-child)': {
      marginLeft: theme.spacing(1),
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginLeft: 0,
    },
  },

  labelFooterButton: {
    width: 150,
    height: 56,
    borderRadius: 500,
    fontSize: 16,
    lineHeight: '20px',
    textTransform: 'none',

    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(1.25),
    },

    '& .MuiSvgIcon-root': {
      width: 21,
      height: 21,
    },

    [theme.breakpoints.down('xs')]: {
      flex: 1,
      width: 'auto',
    },
  },
}));

const Document = ({ document, visible, onInitActions, showInternational, isPrintDocument }) => {
  const [data, setData] = useState<
    { blob: Blob; url: string; originalUrl: string } | { error: string } | null
  >(null);
  const [actions, setActions] = useState<any>(null);

  useEffect(() => {
    if (document.loading) {
      setData(null);
      setActions(null);
      return;
    }

    if (document.blob) {
      setData({
        blob: document.blob,
        url: URL.createObjectURL(document.blob),
        originalUrl: document.url,
      });
    } else {
      fetch(document.url)
        .then((res) => res.blob())
        .then((blob) => {
          setData({
            blob,
            url: URL.createObjectURL(blob),
            originalUrl: document.url,
          });
        })
        .catch((error) => {
          setData({
            error: error.message,
          });
        });
    }

    return () => {
      if (data && 'url' in data) {
        URL.revokeObjectURL(data.url);
      }
    };
  }, [document]); //eslint-disable-line

  useEffect(() => {
    if (visible && actions) {
      onInitActions(actions);
    }
  }, [visible, actions]); //eslint-disable-line

  const handleInitActions = (actions) => setActions(actions);

  return (
    <div style={{ display: visible ? 'block' : 'none' }}>
      <DocumentFrame
        data={document.loading ? null : data}
        onInitActions={handleInitActions}
        showInternational={showInternational}
        isPrintDocument={isPrintDocument}
      />
    </div>
  );
};

const DocumentFrame = ({ data, onInitActions, showInternational, isPrintDocument }) => {
  const classes = useStyles();
  const notify = useCustomNotify();
  const pageRef = useRef<HTMLDivElement>(null);
  const isFirefox = typeof InstallTrigger !== 'undefined';
  const isWindows = navigator.userAgent.toLocaleLowerCase().includes('windows');
  const embedViewClass = isPrintDocument
    ? showInternational
      ? classes.labelInternationalEmbedView
      : classes.labelEmbedView
    : showInternational
    ? classes.internationalEmbedView
    : classes.embedView;

  const handleLoad = (e) => {
    const frame = e.target as HTMLIFrameElement;

    // reference to .print throws a Security Error in Firefox due to bug: https://bugzilla.mozilla.org/show_bug.cgi?id=911444
    try {
      if (!isFirefox && frame.contentWindow && frame.contentWindow.print) {
        onInitActions({
          print: () => {
            try {
              frame.contentWindow?.print();
            } catch (err) {
              notify(err.message, 'error');
            }
          },
        });
      }
      if (isFirefox) {
        onInitActions({
          print: () => {
            try {
              if (data.originalUrl) {
                printPdf(data.originalUrl, notify);
              } else {
                frame.contentWindow?.print();
              }
            } catch (err) {
              notify(err.message, 'error');
            }
          },
        });
      }
    } catch (err) {
      onInitActions({
        download: data.originalUrl,
      });
    }

    if (data.blob.type !== 'text/html') {
      return;
    }

    const html = frame.contentDocument?.querySelector('html');
    const body = frame.contentDocument?.body;

    if (!pageRef.current || !html || !body) {
      return;
    }

    html.style.overflow = 'hidden';

    Object.assign(pageRef.current.style, {
      width: `${body.scrollWidth}px`,
      height: `${body.scrollHeight}px`,
    });
  };

  if (!data) {
    return (
      <div className={classes.loading}>
        <div className={classes.loadingAnimation}>
          <Lottie
            options={printLoadingAnimationOptions}
            width="100%"
            height="100%"
            ariaRole="status"
            ariaLabel="Preparing document for printing"
            isClickToPauseDisabled
          />
        </div>
      </div>
    );
  }

  if (data.error) {
    return (
      <div className={classes.loading}>
        <Typography color="error">{data.error}</Typography>
      </div>
    );
  }

  if (data.blob.type === 'text/html') {
    return (
      <div className={classes.docView}>
        <div ref={pageRef} className={classes.page}>
          <iframe title="PDF" className={classes.frame} src={data.url} onLoad={handleLoad} />
        </div>
      </div>
    );
  }

  if (isFirefox) {
    return (
      <div className={embedViewClass}>
        <object
          className={classes.frame}
          data={data.originalUrl ? data.originalUrl : data.url}
          type="application/pdf">
          <iframe
            title="PDF"
            className={classes.frame}
            src={data.originalUrl ? data.originalUrl : data.url}
            onLoad={handleLoad}
          />
        </object>
      </div>
    );
  }

  if (isWindows) {
    return (
      <div className={embedViewClass}>
        <iframe
          title="PDF"
          className={classes.frame}
          src={`${data.url}#navpanes=0`}
          onLoad={handleLoad}
        />
      </div>
    );
  }

  return (
    <div className={embedViewClass}>
      <object className={classes.frame} data={data.url} type="application/pdf">
        <iframe title="PDF" className={classes.frame} src={data.url} onLoad={handleLoad} />
      </object>
    </div>
  );
};

const Lightbox = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const notify = useCustomNotify();
  const docs = useSelector((state) => state.lightbox.docs, shallowEqual);
  const [tab, setTab] = useState(0);
  const [actions, setActions] = useState<any>(null);
  const [showInternational, setShowInternational] = useState<boolean>(false);
  const isFirefox = typeof InstallTrigger !== 'undefined';
  const doc = docs && docs[tab];

  useEffect(() => {
    const hideInternational = JSON.parse(localStorage.getItem('hideInternational') as string);
    setShowInternational(!hideInternational);
  }, [docs]);

  useEffect(() => {
    setActions(null);
  }, [doc?.id, doc?.loading]);

  const handleClose = () => {
    setTab(0);
    dispatch(closeAll());
  };

  const handleCloseTab = (index) => (e) => {
    e.stopPropagation();
    e.preventDefault();

    setTab(Math.min(tab, docs.length - 2));

    const doc = docs[index];
    dispatch(closeDoc(doc.id));
  };

  const handleChangeTab = (e, index) => {
    setActions(null);
    setTab(index);
  };

  const handleInitActions = (actions) => setActions(actions);

  if (!docs || !docs.length || !doc) {
    return null;
  }

  const isShipmentLabel = doc.documentType === 'shipmentLabel';
  const isPrintDocument = isShipmentLabel || doc.documentType === 'packingSlip';
  const trackingNumbers = (doc.trackingNumbers || []).filter(
    (trackingNumber) => typeof trackingNumber === 'string' && trackingNumber.trim()
  );
  const copyButtonLabel =
    !doc.loading && doc.labelMode === 'single' && trackingNumbers.length
      ? 'Copy tracking number'
      : !doc.loading && doc.labelMode === 'groupedShipments' && trackingNumbers.length
      ? `Copy ${trackingNumbers.length} tracking ${
          trackingNumbers.length === 1 ? 'number' : 'numbers'
        }`
      : null;
  const printButtonLabel = doc.labelMode === 'single' ? 'Print' : 'Print all';

  const onConfirm = () => {
    setShowInternational(false);
  };

  const onCancel = () => {
    setShowInternational(false);
    localStorage.setItem('hideInternational', JSON.stringify(true));
  };

  const handleCopyTrackingNumbers = () => {
    const value = doc.labelMode === 'single' ? trackingNumbers[0] : trackingNumbers.join('\n');

    clipboardCopy(value)
      .then(() => {
        notify(
          doc.labelMode === 'single' ? 'Track number is copied' : 'Tracking numbers are copied'
        );
      })
      .catch((err) => {
        notify(err.message, 'error');
      });
  };

  const renderInternationalNotice = (className) => {
    if (!showInternational || !doc.noticeHtml) {
      return null;
    }

    return (
      <div className={className}>
        <Typography>For international shipments</Typography>
        {ReactHtmlParser(decodeURIComponent(doc.noticeHtml.replace(/\+/g, ' ')))}
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="secondary"
              size="medium"
              fullWidth
              className={classes.internationalButton}
              onClick={onCancel}>
              Don't show this again
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="medium"
              className={classes.internationalButton}
              onClick={onConfirm}>
              Ok
            </Button>
          </Grid>
        </Grid>
      </div>
    );
  };

  return (
    <Dialog
      open={true}
      maxWidth={isPrintDocument ? false : 'sm'}
      onEscapeKeyDown={handleClose}
      PaperProps={{
        className: isPrintDocument ? classes.labelDialog : undefined,
        style: { overflow: 'hidden' },
      }}>
      {isPrintDocument ? (
        <>
          <div className={classes.labelHeader}>
            <Button className={classes.labelCloseButton} aria-label="Close" onClick={handleClose}>
              <CloseOutlined />
            </Button>
          </div>
          {isShipmentLabel && renderInternationalNotice(classes.labelInternationalNotice)}
        </>
      ) : (
        <>
          <Button className={classes.closeButton} onClick={handleClose}>
            <CloseOutlined />
          </Button>
          <DialogTitle disableTypography={true} className={classes.title}>
            <Typography variant="h6">
              {docs.length > 1 ? 'Multiple Documents' : doc.name}
            </Typography>
            {actions && actions.print && (
              <Button
                className={classes.actionButton}
                color="primary"
                size="small"
                variant="contained"
                onClick={actions.print}>
                Print
              </Button>
            )}
            {actions && actions.download && (
              <Button
                className={classes.actionButton}
                component="a"
                color="primary"
                size="small"
                variant="contained"
                href={actions.download}
                target="_blank"
                download>
                {isFirefox ? 'Print' : 'Download'}
              </Button>
            )}
            {renderInternationalNotice(classes.internationalNotice)}
          </DialogTitle>
        </>
      )}
      {!isPrintDocument && docs.length > 1 && (
        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="on"
          onChange={handleChangeTab}>
          {docs.map((doc, i) => (
            <Tab
              key={doc.id}
              value={i}
              label={
                <span className={classes.tabLabel}>
                  <span>{doc.name}</span>
                  <HighlightOffOutlined
                    className={classes.tabClose}
                    fontSize="small"
                    onClick={handleCloseTab(i)}
                  />
                </span>
              }
            />
          ))}
        </Tabs>
      )}
      <div className={isPrintDocument ? classes.labelContent : classes.content}>
        {docs.map((document, index) => (
          <Document
            key={document.id}
            document={document}
            visible={tab === index}
            onInitActions={handleInitActions}
            showInternational={showInternational && Boolean(document.noticeHtml)}
            isPrintDocument={
              document.documentType === 'shipmentLabel' || document.documentType === 'packingSlip'
            }
          />
        ))}
      </div>
      {isPrintDocument && (
        <div className={classes.labelFooter}>
          {copyButtonLabel && (
            <Button
              className={classes.labelCopyButton}
              variant="outlined"
              startIcon={<FileCopyOutlined />}
              onClick={handleCopyTrackingNumbers}>
              {copyButtonLabel}
            </Button>
          )}
          <div className={classes.labelFooterActions}>
            <Button className={classes.labelFooterButton} variant="contained" onClick={handleClose}>
              Close
            </Button>
            {!doc.loading && actions && actions.download && !actions.print ? (
              <Button
                className={classes.labelFooterButton}
                component="a"
                color="primary"
                variant="contained"
                startIcon={<PrintOutlined />}
                href={actions.download}
                target="_blank"
                download>
                {isFirefox ? printButtonLabel : 'Download'}
              </Button>
            ) : (
              <Button
                className={classes.labelFooterButton}
                color="primary"
                variant="contained"
                startIcon={<PrintOutlined />}
                disabled={doc.loading || !actions || !actions.print}
                onClick={!doc.loading && actions ? actions.print : undefined}>
                {printButtonLabel}
              </Button>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default Lightbox;
